import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { isPro } from '../utils/featureGate';

const QUESTIONS = [
  {
    id: 'emergency',
    category: 'Emergency Fund',
    question: 'How many months of expenses do you have saved in an accessible account?',
    options: [
      { label: 'None', value: 0 },
      { label: 'Less than 1 month', value: 20 },
      { label: '1–2 months', value: 50 },
      { label: '3–5 months', value: 80 },
      { label: '6+ months', value: 100 },
    ],
  },
  {
    id: 'credit_card_debt',
    category: 'Debt',
    question: 'Do you carry a credit card balance from month to month (paying interest)?',
    options: [
      { label: 'Yes, significant balance ($5,000+)', value: 0 },
      { label: 'Yes, moderate balance ($1,000–$5,000)', value: 30 },
      { label: 'Yes, small balance (under $1,000)', value: 60 },
      { label: 'No, I pay it off in full every month', value: 100 },
      { label: "I don't have a credit card", value: 80 },
    ],
  },
  {
    id: 'retirement',
    category: 'Retirement',
    question: 'Are you actively saving for retirement?',
    options: [
      { label: 'No — not saving anything for retirement', value: 0 },
      { label: "I have a 401k but don't contribute", value: 10 },
      { label: 'I contribute something but not enough to get the employer match', value: 40 },
      { label: "I contribute enough to get my employer's full match", value: 70 },
      { label: 'I max out my 401k or IRA (or both)', value: 100 },
    ],
  },
  {
    id: 'investing',
    category: 'Investing',
    question: 'Do you have any investments beyond your retirement accounts?',
    options: [
      { label: 'No investments at all', value: 0 },
      { label: 'Only my employer retirement account', value: 40 },
      { label: 'I have a Roth IRA or individual brokerage account', value: 70 },
      { label: 'I invest regularly in multiple account types', value: 100 },
    ],
  },
  {
    id: 'budget',
    category: 'Budget & Cash Flow',
    question: 'Do you know roughly where your money goes each month?',
    options: [
      { label: "I have no idea where my money goes", value: 0 },
      { label: "I have a general sense but don't track it", value: 35 },
      { label: 'I track spending occasionally', value: 60 },
      { label: 'I have a budget and follow it most months', value: 85 },
      { label: 'I track everything and consistently stay within budget', value: 100 },
    ],
  },
  {
    id: 'income_vs_expenses',
    category: 'Budget & Cash Flow',
    question: 'At the end of each month, what typically happens?',
    options: [
      { label: "I've spent more than I earned", value: 0 },
      { label: 'I barely break even', value: 25 },
      { label: 'I have a small amount left over ($1–200)', value: 50 },
      { label: 'I consistently save $200–500+/month', value: 80 },
      { label: 'I save or invest 20%+ of my income consistently', value: 100 },
    ],
  },
  {
    id: 'insurance',
    category: 'Protection',
    question: 'Do you have adequate insurance coverage?',
    options: [
      { label: 'I have no insurance (health, life, disability)', value: 0 },
      { label: 'I have health insurance only', value: 40 },
      { label: 'I have health + life or disability insurance', value: 70 },
      { label: 'I have health, life, and disability insurance', value: 100 },
    ],
  },
  {
    id: 'high_interest_debt',
    category: 'Debt',
    question: 'Do you have any high-interest debt (payday loans, medical debt, personal loans above 10%)?',
    options: [
      { label: 'Yes, significant amount', value: 0 },
      { label: 'Yes, a small amount', value: 40 },
      { label: 'No high-interest debt', value: 100 },
    ],
  },
  {
    id: 'financial_goals',
    category: 'Planning',
    question: 'Do you have clear financial goals with a plan to reach them?',
    options: [
      { label: 'I have no financial goals', value: 0 },
      { label: "I have vague goals but no plan", value: 25 },
      { label: "I have goals but haven't written them down or made a plan", value: 50 },
      { label: 'I have specific goals and a rough plan', value: 75 },
      { label: 'I have specific goals, a written plan, and review it regularly', value: 100 },
    ],
  },
  {
    id: 'knowledge',
    category: 'Financial Knowledge',
    question: 'How confident do you feel about your financial knowledge?',
    options: [
      { label: 'Very unconfident — finance confuses me', value: 10 },
      { label: 'I know basics but get confused by investing/taxes', value: 35 },
      { label: 'I understand most concepts but have gaps', value: 60 },
      { label: 'I feel fairly confident and keep learning', value: 85 },
      { label: 'Very confident — I actively manage and optimize my finances', value: 100 },
    ],
  },
];

// Map question IDs to categories for scoring
const CATEGORY_QUESTIONS = {
  'Emergency Fund': ['emergency'],
  'Debt': ['credit_card_debt', 'high_interest_debt'],
  'Investing': ['investing'],
  'Retirement': ['retirement'],
  'Budget & Cash Flow': ['budget', 'income_vs_expenses'],
  'Protection': ['insurance'],
  'Planning': ['financial_goals', 'knowledge'],
};

const CATEGORY_COLORS = {
  'Emergency Fund': '#22c55e',
  'Debt': '#ef4444',
  'Investing': '#38bdf8',
  'Retirement': '#f59e0b',
  'Budget & Cash Flow': '#a78bfa',
  'Protection': '#60a5fa',
  'Planning': '#34d399',
};

const SCORE_LABELS = {
  90: { label: 'Excellent', color: '#22c55e', desc: 'You\'re in great financial shape. Focus on optimizing and growing.' },
  75: { label: 'Good', color: '#22c55e', desc: 'You\'re doing well. A few targeted improvements could significantly strengthen your position.' },
  60: { label: 'Fair', color: '#f59e0b', desc: 'You have a solid foundation but meaningful gaps that need attention.' },
  40: { label: 'Needs Work', color: '#f59e0b', desc: 'There are important areas to address. Small consistent actions will move the needle quickly.' },
  0:  { label: 'Getting Started', color: '#ef4444', desc: 'Everyone starts somewhere. Use this as a roadmap — each step you take improves your score.' },
};

const IMPROVEMENTS = {
  'Emergency Fund': {
    low: { action: 'Build your emergency fund first', detail: 'Start with $500 as your immediate goal, then grow to 3–6 months of expenses. Open a high-yield savings account to hold it.', link: '/howto', linkLabel: 'Open a HYSA →' },
    high: { action: 'Emergency fund looks solid', detail: 'You have a financial cushion. Keep it in a high-yield savings account to earn interest while it sits there.', link: null },
  },
  'Debt': {
    low: { action: 'Attack high-interest debt urgently', detail: 'Credit card and high-interest debt is one of the biggest obstacles to wealth. Use the avalanche method (highest rate first) or check if you qualify for a 0% balance transfer.', link: '/playbooks', linkLabel: 'Debt Paydown Playbook →' },
    high: { action: 'Debt under control — keep it that way', detail: 'Staying debt-free from high-interest sources is one of the best financial habits you can maintain.', link: null },
  },
  'Investing': {
    low: { action: 'Start investing, even with small amounts', detail: 'After your emergency fund is funded and high-interest debt is cleared, start investing. A Roth IRA with a simple index fund is an excellent starting point.', link: '/howto', linkLabel: 'Open a Roth IRA →' },
    high: { action: 'Keep investing consistently', detail: 'Stay the course. Automate contributions and avoid pulling money out during market downturns.', link: null },
  },
  'Retirement': {
    low: { action: 'Start contributing to retirement immediately', detail: "At minimum, contribute enough to get your employer's full 401k match — that's a guaranteed 50–100% return. Then open a Roth IRA.", link: '/howto', linkLabel: 'How to enroll in a 401k →' },
    high: { action: 'Retirement savings on track', detail: "You're prioritizing long-term financial security. Make sure your investments are in low-cost index funds, not high-fee actively managed funds.", link: null },
  },
  'Budget & Cash Flow': {
    low: { action: 'Get visibility into your cash flow', detail: "You can't improve what you don't measure. Track spending for one month — many people are surprised where their money goes. Start with the 50/30/20 rule as a guide.", link: '/ask', linkLabel: 'Ask AI for budgeting help →' },
    high: { action: 'Strong budgeting habits', detail: 'Keep the consistency going. Consider automating savings before you have a chance to spend them.', link: null },
  },
  'Protection': {
    low: { action: 'Get proper insurance coverage', detail: "Health insurance is non-negotiable. If you have dependents, term life insurance is usually affordable ($20–40/month) and essential. Disability insurance protects your income if you can't work.", link: '/ask', linkLabel: 'Ask AI about insurance →' },
    high: { action: 'Good coverage in place', detail: 'Review your coverage annually. Life changes (marriage, kids, home purchase) usually require coverage updates.', link: null },
  },
  'Planning': {
    low: { action: 'Define clear, specific financial goals', detail: "Vague goals don't get achieved. Write down specific targets: 'Save $10,000 emergency fund by December' or 'Invest $300/month by age 35.' Use our Money Game Plan for a personalized roadmap.", link: '/gameplan', linkLabel: 'Build My Money Game Plan →' },
    high: { action: 'Strong financial planning habits', detail: 'Planning and knowledge are force multipliers. Keep learning and revisiting your plan as life changes.', link: null },
  },
};

function getScoreLabel(score) {
  for (const threshold of [90, 75, 60, 40, 0]) {
    if (score >= threshold) return SCORE_LABELS[threshold];
  }
  return SCORE_LABELS[0];
}

export default function HealthScore() {
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [current, setCurrent] = useState(0);
  const pro = isPro();

  const answer = (questionId, value) => {
    setAnswers(a => ({ ...a, [questionId]: value }));
  };

  const next = () => {
    if (current < QUESTIONS.length - 1) setCurrent(c => c + 1);
  };
  const prev = () => {
    if (current > 0) setCurrent(c => c - 1);
  };

  const allAnswered = QUESTIONS.every(q => answers[q.id] !== undefined);
  const progress = (Object.keys(answers).length / QUESTIONS.length) * 100;

  const getScore = () => {
    const vals = Object.values(answers);
    if (!vals.length) return 0;
    return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
  };

  const getCategoryScore = (cat) => {
    const qIds = CATEGORY_QUESTIONS[cat] || [];
    const vals = qIds.map(id => answers[id]).filter(v => v !== undefined);
    if (!vals.length) return null;
    return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
  };

  const submit = () => {
    if (allAnswered) setSubmitted(true);
  };
  const reset = () => {
    setAnswers({});
    setSubmitted(false);
    setCurrent(0);
  };

  const q = QUESTIONS[current];
  const score = getScore();
  const scoreInfo = getScoreLabel(score);

  if (submitted) {
    return (
      <div style={s.page}>
        <div style={s.glow} />
        <div style={s.inner}>
          {/* Score header */}
          <div style={s.resultHeader}>
            <div style={s.resultEyebrow}>Your Financial Health Score</div>
            <div style={{ ...s.bigScore, color: scoreInfo.color }}>{score}<span style={s.bigScoreOf}>/100</span></div>
            <div style={{ ...s.scoreBadge, background: scoreInfo.color + '18', color: scoreInfo.color, borderColor: scoreInfo.color + '40' }}>{scoreInfo.label}</div>
            <p style={s.scoreDesc}>{scoreInfo.desc}</p>
          </div>

          {/* Category breakdown */}
          <div style={s.breakdownCard}>
            <div style={s.breakdownTitle}>Score Breakdown</div>
            {Object.entries(CATEGORY_QUESTIONS).map(([cat]) => {
              const catScore = getCategoryScore(cat);
              if (catScore === null) return null;
              const color = CATEGORY_COLORS[cat];
              return (
                <div key={cat} style={s.barRow}>
                  <span style={s.barLabel}>{cat}</span>
                  <div style={s.barTrack}>
                    <div style={{ ...s.barFill, width: catScore + '%', background: color }} />
                  </div>
                  <span style={{ ...s.barPct, color }}>{catScore}</span>
                </div>
              );
            })}
          </div>

          {/* Improvement plan — Pro only */}
          <div style={s.improvements}>
            <div style={s.improvTitle}>📋 Your Improvement Roadmap</div>
            <p style={s.improvSub}>Focus on these areas first — sorted by impact:</p>

            {pro ? (
              // Full roadmap for Pro users
              Object.entries(CATEGORY_QUESTIONS)
                .map(([cat]) => {
                  const catScore = getCategoryScore(cat);
                  if (catScore === null) return null;
                  const low = catScore < 65;
                  const imp = IMPROVEMENTS[cat]?.[low ? 'low' : 'high'];
                  if (!imp) return null;
                  return { cat, catScore, imp, low };
                })
                .filter(Boolean)
                .sort((a, b) => a.catScore - b.catScore)
                .map(({ cat, catScore, imp, low }) => (
                  <div key={cat} style={{ ...s.improvCard, borderColor: low ? 'rgba(239,68,68,0.2)' : 'rgba(34,197,94,0.15)' }}>
                    <div style={s.improvCardHeader}>
                      <span style={{ ...s.improvBadge, background: (CATEGORY_COLORS[cat] || '#64748b') + '18', color: CATEGORY_COLORS[cat] || '#64748b', borderColor: (CATEGORY_COLORS[cat] || '#64748b') + '35' }}>{cat} · {catScore}/100</span>
                      {low && <span style={s.priorityTag}>Priority</span>}
                    </div>
                    <div style={s.improvAction}>{low ? '⚠️' : '✅'} {imp.action}</div>
                    <p style={s.improvDetail}>{imp.detail}</p>
                    {imp.link && <Link to={imp.link} style={s.improvLink}>{imp.linkLabel}</Link>}
                  </div>
                ))
            ) : (
              // Free users: show first 2 items blurred, then paywall
              <div style={s.roadsLockWrap}>
                {/* First item visible */}
                {(() => {
                  const sorted = Object.entries(CATEGORY_QUESTIONS)
                    .map(([cat]) => {
                      const catScore = getCategoryScore(cat);
                      if (catScore === null) return null;
                      const low = catScore < 65;
                      const imp = IMPROVEMENTS[cat]?.[low ? 'low' : 'high'];
                      if (!imp) return null;
                      return { cat, catScore, imp, low };
                    })
                    .filter(Boolean)
                    .sort((a, b) => a.catScore - b.catScore);

                  return (
                    <>
                      {/* Show first card normally */}
                      {sorted.slice(0, 1).map(({ cat, catScore, imp, low }) => (
                        <div key={cat} style={{ ...s.improvCard, borderColor: low ? 'rgba(239,68,68,0.2)' : 'rgba(34,197,94,0.15)' }}>
                          <div style={s.improvCardHeader}>
                            <span style={{ ...s.improvBadge, background: (CATEGORY_COLORS[cat] || '#64748b') + '18', color: CATEGORY_COLORS[cat] || '#64748b', borderColor: (CATEGORY_COLORS[cat] || '#64748b') + '35' }}>{cat} · {catScore}/100</span>
                            {low && <span style={s.priorityTag}>Priority</span>}
                          </div>
                          <div style={s.improvAction}>{low ? '⚠️' : '✅'} {imp.action}</div>
                          <p style={s.improvDetail}>{imp.detail}</p>
                          {imp.link && <Link to={imp.link} style={s.improvLink}>{imp.linkLabel}</Link>}
                        </div>
                      ))}
                      {/* Remaining cards blurred */}
                      <div style={s.roadsBlur}>
                        {sorted.slice(1, 4).map(({ cat, catScore, imp, low }) => (
                          <div key={cat} style={{ ...s.improvCard, borderColor: 'rgba(255,255,255,0.06)', pointerEvents: 'none' }}>
                            <div style={s.improvCardHeader}>
                              <span style={{ ...s.improvBadge, background: '#1e293b', color: '#334155', borderColor: '#334155' }}>{cat} · {catScore}/100</span>
                            </div>
                            <div style={{ ...s.improvAction, color: '#334155' }}>{'■'.repeat(imp.action.length)}</div>
                            <p style={{ ...s.improvDetail, color: '#1e293b' }}>{'█'.repeat(80)}</p>
                          </div>
                        ))}
                      </div>
                    </>
                  );
                })()}

                {/* Paywall overlay */}
                <div style={s.roadsWall}>
                  <div style={s.roadsWallIcon}>🔒</div>
                  <h3 style={s.roadsWallTitle}>{Object.keys(CATEGORY_QUESTIONS).length - 1} more improvements waiting for you</h3>
                  <p style={s.roadsWallDesc}>
                    Upgrade to <strong>KnowFi Pro</strong> to unlock your full personalized roadmap — every category scored, every priority ranked, every action step explained.
                  </p>
                  <Link to="/signup?plan=pro" style={s.roadsWallBtn}>Unlock with Pro — $12/mo →</Link>
                  <p style={s.roadsWallNote}>Cancel anytime · All features unlocked</p>
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div style={s.resultActions}>
            {pro ? (
              <>
                <Link to="/gameplan" style={s.btnPrimary}>🎯 Build My Full Money Plan</Link>
                <Link to="/playbooks" style={s.btnSecondary}>📋 Find My Situation Playbook</Link>
              </>
            ) : (
              <Link to="/signup?plan=pro" style={s.btnPrimary}>🔓 Unlock Full Results with Pro →</Link>
            )}
            <button style={s.retakeBtn} onClick={reset}>Retake Quiz</button>
          </div>

          <p style={s.disclaimer}>This score is based on your self-reported answers and general financial benchmarks. It is educational only — not a professional financial assessment.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={s.page}>
      <SEO
        title="Free Financial Health Score — See Where You Stand"
        description="Get your personal financial health score in seconds. See how your emergency fund, debt, savings rate, and investments stack up — and what to fix first."
        path="/healthscore"
        keywords="financial health score, check financial health, emergency fund check, debt ratio, savings rate"
      />
      <div style={s.glow} />
      <div style={s.inner}>
        {/* Header */}
        <div style={s.header}>
          <div style={s.eyebrow}>🏆 Free · 2 minutes</div>
          <h1 style={s.title}>Financial Health Score</h1>
          <p style={s.sub}>10 questions. Find out where you stand and exactly where to improve.</p>
        </div>

        {/* Progress */}
        <div style={s.progressWrap}>
          <div style={s.progressBar}>
            <div style={{ ...s.progressFill, width: progress + '%' }} />
          </div>
          <span style={s.progressLabel}>Question {current + 1} of {QUESTIONS.length}</span>
        </div>

        {/* Question card */}
        <div style={s.questionCard}>
          <div style={s.qCategory}>{q.category}</div>
          <h2 style={s.qText}>{q.question}</h2>
          <div style={s.options}>
            {q.options.map((opt, i) => (
              <button
                key={i}
                style={{ ...s.option, ...(answers[q.id] === opt.value ? s.optionSelected : {}) }}
                onClick={() => answer(q.id, opt.value)}
              >
                <span style={{ ...s.optionDot, ...(answers[q.id] === opt.value ? s.optionDotSelected : {}) }} />
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Navigation */}
        <div style={s.nav}>
          <button style={{ ...s.navBtn, ...(current === 0 ? s.navBtnDisabled : {}) }} onClick={prev} disabled={current === 0}>← Back</button>
          <div style={s.navDots}>
            {QUESTIONS.map((_, i) => (
              <div key={i} style={{ ...s.dot, ...(i === current ? s.dotActive : answers[QUESTIONS[i].id] !== undefined ? s.dotDone : {}) }} onClick={() => setCurrent(i)} />
            ))}
          </div>
          {current < QUESTIONS.length - 1 ? (
            <button
              style={{ ...s.navBtn, ...s.navBtnNext, ...(answers[q.id] === undefined ? s.navBtnDisabled : {}) }}
              onClick={next}
              disabled={answers[q.id] === undefined}
            >Next →</button>
          ) : (
            <button
              style={{ ...s.navBtn, ...s.navBtnSubmit, ...(!allAnswered ? s.navBtnDisabled : {}) }}
              onClick={submit}
              disabled={!allAnswered}
            >{allAnswered ? 'Get My Score →' : `Answer all ${QUESTIONS.length} questions`}</button>
          )}
        </div>

        {!allAnswered && (
          <p style={s.hint}>Answer all 10 questions to see your full score and personalized improvement plan.</p>
        )}
      </div>
    </div>
  );
}

const s = {
  page: { background: '#0f172a', color: '#f8fafc', minHeight: '100vh', padding: '60px 24px', position: 'relative', overflow: 'hidden' },
  glow: { position: 'absolute', top: '-80px', left: '50%', transform: 'translateX(-50%)', width: '800px', height: '500px', background: 'radial-gradient(ellipse at center, rgba(56,189,248,0.1) 0%, transparent 65%)', pointerEvents: 'none' },
  inner: { maxWidth: '640px', margin: '0 auto', position: 'relative', zIndex: 1 },

  header: { textAlign: 'center', marginBottom: '40px' },
  eyebrow: { display: 'inline-block', background: 'rgba(56,189,248,0.1)', border: '1px solid rgba(56,189,248,0.25)', color: '#38bdf8', padding: '5px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', marginBottom: '16px' },
  title: { fontSize: 'clamp(28px,5vw,46px)', fontWeight: '900', margin: '0 0 12px', letterSpacing: '-1px' },
  sub: { color: '#64748b', fontSize: '16px', lineHeight: 1.65 },

  progressWrap: { marginBottom: '28px' },
  progressBar: { background: '#1e293b', borderRadius: '4px', height: '5px', marginBottom: '8px' },
  progressFill: { background: '#38bdf8', height: '100%', borderRadius: '4px', transition: 'width 0.3s' },
  progressLabel: { color: '#475569', fontSize: '12px' },

  questionCard: { background: '#1e293b', borderRadius: '20px', padding: '32px', border: '1px solid #334155', marginBottom: '20px' },
  qCategory: { fontSize: '11px', fontWeight: '700', color: '#38bdf8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' },
  qText: { fontSize: '18px', fontWeight: '700', margin: '0 0 24px', lineHeight: 1.45, color: '#f1f5f9' },
  options: { display: 'flex', flexDirection: 'column', gap: '10px' },
  option: { display: 'flex', alignItems: 'center', gap: '12px', background: '#0f172a', border: '1px solid #334155', borderRadius: '10px', padding: '13px 16px', cursor: 'pointer', textAlign: 'left', color: '#94a3b8', fontSize: '14px', fontFamily: 'inherit', transition: 'all 0.15s' },
  optionSelected: { background: 'rgba(56,189,248,0.08)', border: '1px solid rgba(56,189,248,0.4)', color: '#f1f5f9' },
  optionDot: { width: '16px', height: '16px', borderRadius: '50%', border: '2px solid #334155', flexShrink: 0, transition: 'all 0.15s' },
  optionDotSelected: { background: '#38bdf8', border: '2px solid #38bdf8' },

  nav: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' },
  navBtn: { background: '#1e293b', border: '1px solid #334155', color: '#94a3b8', padding: '10px 20px', borderRadius: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', fontFamily: 'inherit' },
  navBtnNext: { background: 'rgba(56,189,248,0.1)', border: '1px solid rgba(56,189,248,0.3)', color: '#38bdf8' },
  navBtnSubmit: { background: '#38bdf8', border: 'none', color: '#0f172a', padding: '12px 24px', fontWeight: '800' },
  navBtnDisabled: { opacity: 0.35, cursor: 'not-allowed' },
  navDots: { display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'center', flex: 1 },
  dot: { width: '8px', height: '8px', borderRadius: '50%', background: '#1e293b', border: '1px solid #334155', cursor: 'pointer' },
  dotActive: { background: '#38bdf8', border: '1px solid #38bdf8' },
  dotDone: { background: '#22c55e', border: '1px solid #22c55e' },
  hint: { textAlign: 'center', color: '#334155', fontSize: '12px', marginTop: '16px' },

  // Results
  resultHeader: { textAlign: 'center', marginBottom: '40px' },
  resultEyebrow: { fontSize: '12px', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' },
  bigScore: { fontSize: '88px', fontWeight: '900', lineHeight: 1, letterSpacing: '-3px', marginBottom: '16px' },
  bigScoreOf: { fontSize: '28px', color: '#334155', fontWeight: '500' },
  scoreBadge: { display: 'inline-block', border: '1px solid', borderRadius: '20px', padding: '5px 18px', fontSize: '15px', fontWeight: '800', marginBottom: '16px' },
  scoreDesc: { color: '#64748b', fontSize: '16px', lineHeight: 1.6, maxWidth: '480px', margin: '0 auto' },

  breakdownCard: { background: '#1e293b', borderRadius: '16px', padding: '24px', border: '1px solid #334155', marginBottom: '24px' },
  breakdownTitle: { fontSize: '14px', fontWeight: '700', color: '#f1f5f9', marginBottom: '20px' },
  barRow: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' },
  barLabel: { fontSize: '13px', color: '#94a3b8', width: '130px', flexShrink: 0 },
  barTrack: { flex: 1, background: '#0f172a', borderRadius: '4px', height: '8px' },
  barFill: { height: '100%', borderRadius: '4px', transition: 'width 0.6s ease' },
  barPct: { fontSize: '13px', fontWeight: '700', width: '28px', textAlign: 'right', flexShrink: 0 },

  improvements: { marginBottom: '32px' },
  improvTitle: { fontSize: '16px', fontWeight: '700', color: '#f1f5f9', marginBottom: '6px' },
  improvSub: { color: '#64748b', fontSize: '13px', marginBottom: '16px' },
  improvCard: { background: '#1e293b', borderRadius: '14px', padding: '20px', border: '1px solid', marginBottom: '12px' },
  improvCardHeader: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', flexWrap: 'wrap' },
  improvBadge: { display: 'inline-block', border: '1px solid', borderRadius: '20px', padding: '3px 10px', fontSize: '11px', fontWeight: '700' },
  priorityTag: { background: 'rgba(239,68,68,0.1)', color: '#f87171', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '20px', padding: '3px 10px', fontSize: '11px', fontWeight: '700' },
  improvAction: { fontSize: '15px', fontWeight: '700', color: '#f1f5f9', marginBottom: '6px' },
  improvDetail: { color: '#64748b', fontSize: '13px', lineHeight: 1.6, margin: '0 0 12px' },
  improvLink: { color: '#38bdf8', textDecoration: 'none', fontSize: '13px', fontWeight: '700' },

  resultActions: { display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '24px', justifyContent: 'center' },
  btnPrimary: { background: '#38bdf8', color: '#0f172a', padding: '13px 24px', borderRadius: '10px', fontWeight: '700', fontSize: '15px', textDecoration: 'none', display: 'inline-block' },
  btnSecondary: { background: 'transparent', border: '2px solid rgba(56,189,248,0.3)', color: '#38bdf8', padding: '13px 24px', borderRadius: '10px', fontWeight: '700', fontSize: '15px', textDecoration: 'none', display: 'inline-block' },
  retakeBtn: { background: 'transparent', border: '1px solid #334155', color: '#64748b', padding: '13px 24px', borderRadius: '10px', fontWeight: '600', fontSize: '15px', cursor: 'pointer', fontFamily: 'inherit' },
  disclaimer: { color: '#334155', fontSize: '12px', textAlign: 'center', lineHeight: 1.6 },

  roadsLockWrap: { position: 'relative' },
  roadsBlur: { filter: 'blur(4px)', pointerEvents: 'none', userSelect: 'none', opacity: 0.5 },
  roadsWall: { background: 'linear-gradient(135deg, rgba(56,189,248,0.06), rgba(99,102,241,0.06))', border: '2px solid rgba(56,189,248,0.25)', borderRadius: '16px', padding: '32px', textAlign: 'center', marginTop: '12px' },
  roadsWallIcon: { fontSize: '32px', marginBottom: '10px' },
  roadsWallTitle: { fontSize: '20px', fontWeight: '800', color: '#f1f5f9', margin: '0 0 10px', letterSpacing: '-0.3px' },
  roadsWallDesc: { color: '#94a3b8', fontSize: '14px', lineHeight: 1.65, margin: '0 0 20px', maxWidth: '420px', display: 'block', marginLeft: 'auto', marginRight: 'auto' },
  roadsWallBtn: { display: 'inline-block', background: '#38bdf8', color: '#0f172a', padding: '14px 32px', borderRadius: '10px', fontWeight: '800', fontSize: '16px', textDecoration: 'none' },
  roadsWallNote: { color: '#334155', fontSize: '12px', marginTop: '12px', marginBottom: 0 },
};
